import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ErrorOutlined from "@mui/icons-material/ErrorOutline";
import { useAuth } from "react-oidc-context";
import {
  FavoritePriceShowAs,
  IFavoriteLocationExtension,
  IFavoritePriceValue,
  ModeFilter,
  ModeValueRow,
  MonthColumn,
  MonthHalf,
  OriginTypeFilter,
  SendPanelCustomRow,
} from "../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../types/IMonth";
import "./DesktopOriginManagerPanel.css";
import "./ProductGuidancePanel.css";
import { DesktopOriginManagerPanelProps } from "../../../types/IOriginManager";
import {
  applySectionOrderNumbers,
  getActiveOrigins,
  getCenteringScrollOffset,
  getDefaultProductSectionOrder,
  getMarketerIdentifierFromProfile,
  getRowKey,
} from "./FavoriteOriginHelper";
import { formatPriceByShowAs, parsePriceInput } from "./priceFormatting";
import OriginManagerHeader, {
  AddOriginOption,
} from "./components/OriginManagerHeader";
import OriginManagerFilters from "./components/OriginManagerFilters";
import SaveDiscardModal from "./components/SaveDiscardModal";
import DeleteUndoToast from "./components/DeleteUndoToast";
import OriginManagerBottomScrollbar from "./components/OriginManagerBottomScrollbar";
import OriginManagerTableHeader from "./components/OriginManagerTableHeader";
import { KochGuidanceInfoRow } from "./components/OriginManagerInfoRows";
import OriginRow from "./components/OriginRow";
import { useOriginManagerMonths } from "./hooks/useOriginManagerMonths";
import { useMonthScrollSync } from "./hooks/useMonthScrollSync";
import { useOriginManagerRows } from "./hooks/useOriginManagerRows";
import { useOriginManagerSectionOrder } from "./hooks/useOriginManagerSectionOrder";
import { useOriginManagerDelete } from "./hooks/useOriginManagerDelete";
import { useOriginManagerAddOrigin } from "./hooks/useOriginManagerAddOrigin";
import { useOriginManagerPriceEditing } from "./hooks/useOriginManagerPriceEditing";
import { useNitroStore } from "../../../store/Store";
import ProductGuidancePanel from "./ProductGuidancePanel";
import { usePriceDragCopy } from "../../../hooks/usePriceDragCopy";
import {
  createFavorites,
  persistAdditionalTypes,
  refreshFavoritesByMarketer,
  reorderFavorites,
} from "./originManagerCrudActions";
import {
  AddProductPromptModal,
  useAddOriginProductManager,
} from "./AddProductPromptManager";
import { AddOriginTourOverlay } from "./AddOriginTourOverlay";
import { useAddOriginTour } from "./useAddOriginTour";

// TODO: error messages for certain filter cases?
// TODO: arrow for moving backwards on month select

// month constants
const MONTHS = MONTHS_LOCAL;

const DesktopOriginManagerPanel: React.FC<DesktopOriginManagerPanelProps> = ({
  selectedProduct: product,
  priorityProducts,
  kochOrigins,
  competitorOrigins,
  onProductSelect,
  onSaveComplete,
}) => {
  const auth = useAuth();

  const {
    currentMonth,
    visibleMonths,
    selectedMonths,
    toggleMonth,
    getMonthLabel,
    monthWindowScrollRef,
    canScrollMonthsBack,
    canScrollMonthsForward,
    updateMonthWindowScrollState,
    handleScrollMonthsBack,
    handleScrollMonthsForward,
  } = useOriginManagerMonths();

  // FILTERS
  const [modeFilter, setModeFilter] = useState<ModeFilter>("all");
  const [originTypeFilter, setOriginTypeFilter] =
    useState<OriginTypeFilter>("all");

  const [, setJustAddedByProduct] = useState<Record<string, boolean>>({});
  const [showSendPanel, setShowSendPanel] = useState(false);
  const [sendPanelOrigin, setSendPanelOrigin] =
    useState<IFavoriteLocationExtension | null>(null);
  const [isOriginFilterOpen, setIsOriginFilterOpen] = useState(false);
  const [originFilterText, setOriginFilterText] = useState("");
  const {
    addOriginButtonRef,
    isAddOriginTourVisible,
    addOriginTourSpotlightRect,
    dismissAddOriginTour,
  } = useAddOriginTour({
    selectedProduct: product,
    kochOrigins,
    competitorOrigins,
  });
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Tracks which origin IDs are currently in editing mode (newly added, not yet Done'd).
  // Using state (not ref) so the rows memo reacts when editing mode ends.
  const [editingNewlyAddedIds, setEditingNewlyAddedIds] = useState<Set<string>>(
    new Set(),
  );
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const { bottomMonthScrollbarRef, syncMonthScroll, registerMonthScroller } =
    useMonthScrollSync();
  // Keyed by rowKey (see getRowKey) so we can look up a row's DOM node to scroll it into view.
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const registerRowRef = useCallback(
    (key: string) => (node: HTMLDivElement | null) => {
      if (node) {
        rowRefs.current.set(key, node);
      } else {
        rowRefs.current.delete(key);
      }
    },
    [],
  );

  const [
    originManagerUnsavedNavAttemptNonce,
    originManagerUnsavedNavAttemptSource,
    setOriginManagerHasUnsavedPriceEdits,
    triggerOriginManagerUnsavedNavResolution,
    triggerOriginManagerCloseRequest,
    setOriginManagerLiveSplitMonthKeys,
    clearOriginManagerLiveSplitMonthKeys,
    openInsightsPanel,
    savedNotification,
    failedNotification,
    setFavoriteOrigins,
    setOriginManagerIsSyncing,
  ] = useNitroStore((state) => [
    state.originManagerUnsavedNavAttemptNonce,
    state.originManagerUnsavedNavAttemptSource,
    state.setOriginManagerHasUnsavedPriceEdits,
    state.triggerOriginManagerUnsavedNavResolution,
    state.triggerOriginManagerCloseRequest,
    state.setOriginManagerLiveSplitMonthKeys,
    state.clearOriginManagerLiveSplitMonthKeys,
    state.openInsightsPanel,
    state.savedNotification,
    state.failedNotification,
    state.setFavoriteOrigins,
    state.setOriginManagerIsSyncing,
  ]);
  const handledUnsavedNavAttemptNonceRef = useRef(
    originManagerUnsavedNavAttemptNonce,
  );
  const pendingBackgroundOpsRef = useRef<Set<Promise<unknown>>>(new Set());
  const trackBackgroundOp = useCallback(
    <T,>(operation: Promise<T>): Promise<T> => {
      pendingBackgroundOpsRef.current.add(operation);
      setOriginManagerIsSyncing(true);
      const settle = () => {
        pendingBackgroundOpsRef.current.delete(operation);
        if (pendingBackgroundOpsRef.current.size === 0) {
          setOriginManagerIsSyncing(false);
        }
      };
      operation.then(settle, settle);
      return operation;
    },
    [setOriginManagerIsSyncing],
  );

  // Get all options for the add origin search.
  const getMarketerIdentifier = useCallback(() => {
    return getMarketerIdentifierFromProfile(
      auth.user?.profile as Record<string, unknown> | undefined,
    );
  }, [auth.user?.profile]);

  const refreshFavorites = useCallback(
    async (marketer: string) => {
      return refreshFavoritesByMarketer(marketer, setFavoriteOrigins);
    },
    [setFavoriteOrigins],
  );

  const {
    sectionOrderByProduct,
    setSectionOrderByProduct,
    setPendingOrderSaveByProduct,
    dragState,
    dragOverRowKey,
    handleDragStart,
    handleRowDragOver,
    handleRowDrop,
    handleDragEnd,
    savePendingOrderInBackground,
  } = useOriginManagerSectionOrder({
    selectedProduct: product,
    getMarketerIdentifier,
    refreshFavorites,
  });

  // Combine koch and competitor origins from the server into a single sorted array.
  const {
    savedOrigins,
    newlyAddedOrigins,
    setNewlyAddedOriginsByProduct,
    deletedRowKeysByProduct,
    setDeletedRowKeysByProduct,
    rows,
    filteredRows,
  } = useOriginManagerRows({
    kochOrigins,
    competitorOrigins,
    selectedProduct: product,
    modeFilter,
    originTypeFilter,
    originFilterText,
    editingNewlyAddedIds,
    sectionOrderByProduct,
    setSectionOrderByProduct,
  });

  const {
    isAddOriginOpen,
    setIsAddOriginOpen,
    addOriginQuery,
    setAddOriginQuery,
    optionsLoading,
    isAddingOriginLoading,
    setIsAddingOriginLoading,
    setFavoriteOriginOptions,
    filteredAddOriginOptions,
    addOriginPopoverRef,
    addOriginInputRef,
  } = useOriginManagerAddOrigin({
    kochOrigins,
    competitorOrigins,
    selectedProduct: product,
    savedOrigins,
    newlyAddedOrigins,
    deletedRowKeysByProduct,
  });

  // Derives the mode-specific price rows (posted/truck/rail) shown for an origin.
  // TODO: check month dates are created as YYYY-MM-DD strings
  // TODO: do we need to do something about having ids and everything passed thru when we create new instances
  const getModeRows = useCallback(
    (
      origin: IFavoriteLocationExtension,
      monthChip?: string,
      half: MonthHalf = "full",
    ): ModeValueRow[] => {
      const chipMonthIdx =
        monthChip !== undefined ? MONTHS.indexOf(monthChip) : -1;
      const monthNum = chipMonthIdx >= 0 ? chipMonthIdx + 1 : null;

      const monthEntry =
        monthNum !== null
          ? origin.months.find((m) => m.parsedMonth.month === monthNum) ?? null
          : origin.months[0] ?? null;
      const periodIndex = half === "h2" ? 1 : 0;
      const prices = monthEntry?.periods?.[periodIndex] ?? null;
      const readPriceMeta = (
        priceData: IFavoritePriceValue | null | undefined,
      ): {
        value: number | null;
        showAs: FavoritePriceShowAs | null;
        requestedBy?: string;
        requestedAt?: string;
      } => {
        if (!priceData) {
          return { value: null, showAs: null };
        }
        if (!priceData.price) {
          return {
            value: null,
            showAs: priceData.showAs,
            requestedBy: priceData.updatedBy,
            requestedAt: priceData.updatedAt,
          };
        }
        return {
          value: priceData.price,
          showAs: priceData.showAs,
          requestedBy: priceData.updatedBy,
          requestedAt: priceData.updatedAt,
        };
      };

      const modeRows: ModeValueRow[] = [];

      const outboundModes = origin.location.outboundModes
        .map((mode) => mode.trim().toUpperCase())
        .filter(Boolean);
      const hasTruckOutbound = outboundModes.includes("TRUCK");
      const hasRailOutbound = outboundModes.includes("RAIL");
      const postedMeta = readPriceMeta(prices?.posted ?? null);
      const truckMeta = readPriceMeta(prices?.truck ?? null);
      const railMeta = readPriceMeta(prices?.rail ?? null);

      if (!origin.competitor) {
        modeRows.push({
          key: "posted",
          label: "Posted Px",
          value: postedMeta.value,
          showAs: postedMeta.showAs,
          icon: null,
          requestedBy: postedMeta.requestedBy,
          requestedAt: postedMeta.requestedAt,
        });
      }

      if (modeFilter === "truck") {
        if (hasTruckOutbound) {
          modeRows.push({
            key: "truck",
            label: origin.competitor ? "Price" : "ALT",
            value: truckMeta.value,
            showAs: truckMeta.showAs,
            icon: "truck",
            requestedBy: truckMeta.requestedBy,
            requestedAt: truckMeta.requestedAt,
          });
        }
      } else if (hasTruckOutbound && modeFilter === "all") {
        modeRows.push({
          key: "truck",
          label: origin.competitor ? "Price" : "ALT",
          value: truckMeta.value,
          showAs: truckMeta.showAs,
          icon: "truck",
          requestedBy: truckMeta.requestedBy,
          requestedAt: truckMeta.requestedAt,
        });
      }

      if (modeFilter === "rail") {
        if (hasRailOutbound) {
          modeRows.push({
            key: "rail",
            label: origin.competitor ? "Price" : "ALT",
            value: railMeta.value,
            showAs: railMeta.showAs,
            icon: "rail",
            requestedBy: railMeta.requestedBy,
            requestedAt: railMeta.requestedAt,
          });
        }
      } else if (hasRailOutbound && modeFilter === "all") {
        modeRows.push({
          key: "rail",
          label: origin.competitor ? "Price" : "ALT",
          value: railMeta.value,
          showAs: railMeta.showAs,
          icon: "rail",
          requestedBy: railMeta.requestedBy,
          requestedAt: railMeta.requestedAt,
        });
      }

      return modeRows;
    },
    [modeFilter],
  );

  const {
    setEditedSplitMonthsByProduct,
    isEditingPrices,
    editingProductKey,
    draftPrices,
    setDraftPrices,
    draftPriceShowAs,
    setDraftPriceShowAs,
    recentlySavedKeys,
    editingRowKey,
    activeCellKey,
    setActiveCellKey,
    setPendingRowKey,
    setPendingProductKey,
    showSaveDiscard,
    setShowSaveDiscard,
    setIsEditingPrices,
    setEditingProductKey,
    setEditingRowKey,
    setEditingSplitMonths,
    priceInputRefs,
    getSplitMonthsForRow,
    activeSplitMonths,
    getPriceInputKey,
    getPriceInputValue,
    getPriceShowAsValue,
    getDisplayPriceValue,
    handlePriceInputChange,
    handlePriceInputKeyDown,
    handlePriceInputBlur,
    handlePriceShowAsChange,
    handlePriceShowAsChangeAndSave,
    handlePriceCellClick,
    handleCancelSaveDiscard,
    handleDiscardEdits,
    handleSaveEdits,
    handleToggleSplitMonth,
    handleRevertEditingPrices,
    handleDoneEditingPrices,
  } = useOriginManagerPriceEditing({
    selectedProduct: product,
    selectedMonths,
    currentMonth,
    rows,
    filteredRows,
    newlyAddedOrigins,
    setNewlyAddedOriginsByProduct,
    setEditingNewlyAddedIds,
    setJustAddedByProduct,
    getModeRows,
    getMarketerIdentifier,
    refreshFavorites,
    onSaveComplete,
    onProductSelect,
    failedNotification,
    trackBackgroundOp,
    savedOrigins,
    deletedRowKeysByProduct,
    savePendingOrderInBackground,
    setPendingOrderSaveByProduct,
    originManagerUnsavedNavAttemptSource,
    triggerOriginManagerUnsavedNavResolution,
    triggerOriginManagerCloseRequest,
    setOriginManagerLiveSplitMonthKeys,
    clearOriginManagerLiveSplitMonthKeys,
    panelRef,
  });

  // Auto-scrolls the newly-active editing row to the vertical center of the
  // table so a user clicking a price cell is taken straight to that origin,
  // even if it's scrolled out of view. Runs after render so it measures the
  // row's final (possibly taller, "editing") layout rather than a stale one.
  const previousEditingRowKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (editingRowKey && editingRowKey !== previousEditingRowKeyRef.current) {
      const container = tableScrollRef.current;
      const row = rowRefs.current.get(editingRowKey);
      if (container && row) {
        const centeringOffset = getCenteringScrollOffset(
          container.getBoundingClientRect(),
          row.getBoundingClientRect(),
        );
        container.scrollBy({ top: centeringOffset, behavior: "smooth" });
      }
    }
    previousEditingRowKeyRef.current = editingRowKey;
  }, [editingRowKey]);

  const { pendingUndoDelete, handleRequestDeleteOrigin, handleUndoDelete } =
    useOriginManagerDelete({
      selectedProduct: product,
      savedOrigins,
      newlyAddedOrigins,
      setNewlyAddedOriginsByProduct,
      deletedRowKeysByProduct,
      setDeletedRowKeysByProduct,
      setEditedSplitMonthsByProduct,
      getMarketerIdentifier,
      refreshFavorites,
      onSaveComplete,
      editingRowKey,
      onDeleteActiveEditingRow: () => {
        setEditingRowKey(null);
        setActiveCellKey(null);
        setIsEditingPrices(false);
        setEditingProductKey(null);
        setEditingSplitMonths([]);
      },
    });

  const handlePriceDragCommit = useCallback(
    (
      prices: Record<string, string>,
      showAs: Record<string, FavoritePriceShowAs>,
    ) => {
      handleDoneEditingPrices({
        prices,
        showAs,
        exitEditMode: false,
      });
    },
    [handleDoneEditingPrices],
  );

  const {
    isPriceDragCopying,
    dragHoverPriceKey,
    startPriceDragCopy,
    applyDraggedPriceToInput,
  } = usePriceDragCopy(
    setDraftPriceShowAs,
    setDraftPrices,
    parsePriceInput,
    handlePriceDragCommit,
  );

  useEffect(() => {
    if (!isEditingPrices) {
      return undefined;
    }

    const handlePageWhitespacePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (
        target?.closest(
          'input, textarea, select, button, a, [contenteditable="true"], [role="button"], [role="dialog"], [role="listbox"], [role="menu"]',
        )
      ) {
        return;
      }

      handleDoneEditingPrices();
    };

    document.addEventListener("pointerdown", handlePageWhitespacePointerDown);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePageWhitespacePointerDown,
      );
    };
  }, [handleDoneEditingPrices, isEditingPrices]);

  // Flush any pending row-order save when the panel's close button is clicked.
  useEffect(() => {
    const handleCloseButtonPointerDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest(".origin-manager-close-btn")) {
        return;
      }

      savePendingOrderInBackground(
        product,
        savedOrigins,
        newlyAddedOrigins,
        deletedRowKeysByProduct,
      );
    };

    document.addEventListener("mousedown", handleCloseButtonPointerDown);
    return () => {
      document.removeEventListener("mousedown", handleCloseButtonPointerDown);
    };
  }, [
    savePendingOrderInBackground,
    product,
    savedOrigins,
    newlyAddedOrigins,
    deletedRowKeysByProduct,
  ]);

  useEffect(() => {
    savePendingOrderInBackground(
      product,
      savedOrigins,
      newlyAddedOrigins,
      deletedRowKeysByProduct,
    );
  }, [
    deletedRowKeysByProduct,
    newlyAddedOrigins,
    savedOrigins,
    savePendingOrderInBackground,
    sectionOrderByProduct,
    product,
  ]);

  // Update the global unsaved edits flag whenever a user starts or stops editing prices.
  useEffect(() => {
    setOriginManagerHasUnsavedPriceEdits(isEditingPrices);
  }, [isEditingPrices, setOriginManagerHasUnsavedPriceEdits]);

  // Reset the unsaved edits flag when the component unmounts, so that if the user navigates away from the origin manager, it doesn't incorrectly indicate that there are unsaved edits.
  useEffect(
    () => () => {
      setOriginManagerHasUnsavedPriceEdits(false);
      setOriginManagerIsSyncing(false);
    },
    [setOriginManagerHasUnsavedPriceEdits, setOriginManagerIsSyncing],
  );

  // If the user attempts to navigate away from the origin manager while there are unsaved edits, show the save/discard prompt.
  useEffect(() => {
    // If the nonce is 0, it means there is no unsaved navigation attempt, so we can ignore it.
    if (!originManagerUnsavedNavAttemptNonce) {
      return;
    }

    // If we've already handled this nonce, we don't need to do anything.
    if (
      handledUnsavedNavAttemptNonceRef.current ===
      originManagerUnsavedNavAttemptNonce
    ) {
      return;
    }

    // Mark this nonce as handled, so we don't show the prompt multiple times for the same navigation attempt.
    handledUnsavedNavAttemptNonceRef.current =
      originManagerUnsavedNavAttemptNonce;

    if (originManagerUnsavedNavAttemptSource === "close") {
      if (isEditingPrices) {
        handleSaveEdits();
      } else {
        triggerOriginManagerCloseRequest();
      }
      return;
    }

    // If the user is not currently editing prices, we don't need to show the prompt.
    if (!isEditingPrices) {
      return;
    }

    // Show the save/discard prompt.
    setPendingRowKey(null);
    setPendingProductKey(null);
    setShowSaveDiscard(true);
  }, [
    originManagerUnsavedNavAttemptNonce,
    originManagerUnsavedNavAttemptSource,
    isEditingPrices,
    handleSaveEdits,
    triggerOriginManagerCloseRequest,
    setPendingProductKey,
    setPendingRowKey,
    setShowSaveDiscard,
  ]);

  const handleSaveAdditionalTypes = useCallback(
    async (
      customRows: SendPanelCustomRow[],
      monthColumns: MonthColumn[],
    ): Promise<void> => {
      if (!sendPanelOrigin) return;
      const marketer = getMarketerIdentifier();
      if (!marketer) return;

      await persistAdditionalTypes(
        marketer,
        product,
        sendPanelOrigin,
        customRows,
        monthColumns,
      );
      await refreshFavorites(marketer);

      onSaveComplete?.();
    },
    [
      product,
      sendPanelOrigin,
      getMarketerIdentifier,
      refreshFavorites,
      onSaveComplete,
    ],
  );

  const handlePersistAddedOrigins = useCallback(
    async (rowsToAdd: IFavoriteLocationExtension[]) => {
      if (rowsToAdd.length === 0) {
        return;
      }

      const marketer = getMarketerIdentifier();
      if (!marketer) {
        return;
      }

      try {
        const deletedRowKeys = new Set(
          deletedRowKeysByProduct[product] ?? new Set<string>(),
        );
        rowsToAdd.forEach((row) => {
          deletedRowKeys.delete(getRowKey(row));
        });
        const currentNewRows = newlyAddedOrigins[product] ?? [];
        const favoritesToPersist = getActiveOrigins(
          savedOrigins,
          [...rowsToAdd, ...currentNewRows],
          deletedRowKeys,
        );
        const currentSectionOrder =
          sectionOrderByProduct[product] ?? getDefaultProductSectionOrder();
        const favoritesWithOrder = applySectionOrderNumbers(
          favoritesToPersist,
          currentSectionOrder,
        );

        const createdIdsByRowKey = await createFavorites(
          marketer,
          product,
          rowsToAdd,
          favoritesWithOrder,
          getRowKey,
        );

        await reorderFavorites(
          marketer,
          product,
          favoritesWithOrder,
          getRowKey,
          createdIdsByRowKey,
        );

        // New origins default to "Use price" in the UI; PeriodPrice nodes are
        // only created once the user actually enters a price.

        const refreshedFavorites = await refreshFavorites(marketer);

        if (refreshedFavorites.length > 0) {
          setFavoriteOrigins(refreshedFavorites);
        }

        const persistedProductFavorites = refreshedFavorites.find(
          (entry) => entry.product === product,
        )?.favoriteLocations;
        const persistedRowKeys = new Set(
          persistedProductFavorites?.map((row) => getRowKey(row)),
        );
        const persistedAddedLocationIds = new Set(
          rowsToAdd
            .filter((row) => persistedRowKeys.has(getRowKey(row)))
            .map((row) => row.location.id),
        );

        setNewlyAddedOriginsByProduct((prev) => {
          const productRows = prev[product] ?? [];
          const remainingRows = productRows.filter(
            (row) => !persistedRowKeys.has(getRowKey(row)),
          );

          if (remainingRows.length === productRows.length) {
            return prev;
          }

          return {
            ...prev,
            [product]: remainingRows,
          };
        });

        setEditingNewlyAddedIds((prev) => {
          const next = new Set(prev);
          let changed = false;
          rowsToAdd.forEach((row) => {
            if (
              persistedAddedLocationIds.has(row.location.id) &&
              next.delete(row.location.id)
            ) {
              changed = true;
            }
          });
          return changed ? next : prev;
        });

        setJustAddedByProduct((prev) => ({
          ...prev,
          [product]: false,
        }));
      } catch (error) {
        console.error(
          "Failed to save and refresh favorite origins after add.",
          error,
        );
        failedNotification(
          "Failed to save origin",
          "We could not save this origin. Please try again.",
        );
      }
    },
    [
      getMarketerIdentifier,
      deletedRowKeysByProduct,
      product,
      newlyAddedOrigins,
      savedOrigins,
      sectionOrderByProduct,
      refreshFavorites,
      setNewlyAddedOriginsByProduct,
      setFavoriteOrigins,
      failedNotification,
    ],
  );

  const {
    handleAddOriginOptionClick,
    pendingAddProductPrompt,
    isAddingMissingProduct,
    selectedAddProductOccupantKeys,
    hasAddProductOccupantSelectionChanged,
    closeAddProductPrompt,
    toggleAddProductOccupantSelection,
    handleConfirmAddProductForOrigin,
  } = useAddOriginProductManager({
    product,
    selectedMonths,
    currentMonth,
    newlyAddedOrigins,
    onOriginRowsAdded: handlePersistAddedOrigins,
    setDeletedRowKeysByProduct,
    setNewlyAddedOriginsByProduct,
    setEditingNewlyAddedIds,
    setJustAddedByProduct,
    setAddOriginQuery,
    setOriginTypeFilter,
    setIsAddOriginOpen,
    setFavoriteOriginOptions,
    savedNotification,
    failedNotification,
  });

  const monthColumns = useMemo<MonthColumn[]>(() => {
    return selectedMonths.reduce<MonthColumn[]>((acc, month) => {
      if (activeSplitMonths.includes(month)) {
        acc.push(
          { month, half: "h1", isSplit: true },
          { month, half: "h2", isSplit: true },
        );
      } else {
        acc.push({ month, half: "full", isSplit: false });
      }
      return acc;
    }, []);
  }, [activeSplitMonths, selectedMonths]);
  const monthPaneMinWidthPercent = Math.min(monthColumns.length * 12.5, 50);

  const monthGridTemplateColumns = monthColumns
    .map((column) =>
      column.isSplit ? "minmax(88px, 0.8fr)" : "minmax(96px, 1fr)",
    )
    .join(" ");

  useEffect(() => {
    syncMonthScroll(
      "__effect__",
      bottomMonthScrollbarRef.current?.scrollLeft ?? 0,
    );
  }, [monthColumns, rows, syncMonthScroll, bottomMonthScrollbarRef]);

  const hasSplitMonthColumns = monthColumns.some((column) => column.isSplit);
  const showKochInfoRow =
    (originTypeFilter === "all" || originTypeFilter === "koch") &&
    filteredRows.some((row) => !row.competitor);

  return (
    <div className="desktop-origin-manager-panel" ref={panelRef}>
      <OriginManagerHeader
        addOriginButtonRef={addOriginButtonRef}
        isAddOriginTourVisible={isAddOriginTourVisible}
        onAddOriginButtonClick={() => {
          setIsAddOriginOpen((prev) => !prev);
          if (isAddOriginTourVisible) {
            dismissAddOriginTour();
          }
        }}
        isAddOriginOpen={isAddOriginOpen}
        addOriginPopoverRef={addOriginPopoverRef}
        addOriginInputRef={addOriginInputRef}
        addOriginQuery={addOriginQuery}
        onAddOriginQueryChange={setAddOriginQuery}
        onClearAddOriginSearch={() => {
          setAddOriginQuery("");
          setIsAddOriginOpen(false);
        }}
        optionsLoading={optionsLoading}
        isAddingOriginLoading={isAddingOriginLoading}
        filteredAddOriginOptions={filteredAddOriginOptions}
        onSelectAddOriginOption={(option: AddOriginOption) => {
          setIsAddingOriginLoading(true);
          handleAddOriginOptionClick(option)
            .catch(() => {})
            .finally(() => {
              setIsAddingOriginLoading(false);
            });
        }}
      />

      <OriginManagerFilters
        priorityProducts={priorityProducts}
        selectedProduct={product}
        onProductClick={(item) => {
          if (item === product) {
            return;
          }

          if (isEditingPrices) {
            setPendingRowKey(null);
            setPendingProductKey(item);
            setShowSaveDiscard(true);
            return;
          }

          onProductSelect(item);
        }}
        visibleMonths={visibleMonths}
        selectedMonths={selectedMonths}
        onToggleMonth={toggleMonth}
        monthWindowScrollRef={monthWindowScrollRef}
        onMonthWindowScroll={updateMonthWindowScrollState}
        canScrollMonthsBack={canScrollMonthsBack}
        canScrollMonthsForward={canScrollMonthsForward}
        onScrollMonthsBack={handleScrollMonthsBack}
        onScrollMonthsForward={handleScrollMonthsForward}
        modeFilter={modeFilter}
        onModeFilterChange={setModeFilter}
        originTypeFilter={originTypeFilter}
        onOriginTypeFilterChange={setOriginTypeFilter}
      />

      <div
        className={`dom-table-card dom-table-card--months-dynamic${
          isEditingPrices ? " dom-table-card--editing" : ""
        }`}
        style={
          {
            "--dom-month-pane-min": `${monthPaneMinWidthPercent}%`,
            "--dom-sticky-header-height": hasSplitMonthColumns
              ? "60px"
              : "40px",
            "--dom-first-origin-offset": hasSplitMonthColumns
              ? `calc(var(--dom-info-row-height, 32px) - 10px)`
              : "0px",
          } as React.CSSProperties
        }
      >
        <div ref={tableScrollRef} className="dom-table-scroll">
          {/* Table Header */}
          <OriginManagerTableHeader
            hasSplitMonthColumns={hasSplitMonthColumns}
            isOriginFilterOpen={isOriginFilterOpen}
            onOpenOriginFilter={() => setIsOriginFilterOpen(true)}
            onCloseOriginFilter={() => {
              setIsOriginFilterOpen(false);
              setOriginFilterText("");
            }}
            originFilterText={originFilterText}
            onOriginFilterTextChange={setOriginFilterText}
            monthColumns={monthColumns}
            monthGridTemplateColumns={monthGridTemplateColumns}
            getMonthLabel={getMonthLabel}
            registerMonthScroller={registerMonthScroller}
            onMonthScroll={syncMonthScroll}
          />

          {showKochInfoRow && (
            <KochGuidanceInfoRow
              monthGridTemplateColumns={monthGridTemplateColumns}
              registerMonthScroller={registerMonthScroller}
              onMonthScroll={syncMonthScroll}
            />
          )}

          {filteredRows.length === 0 && (
            <div className="dom-empty-state dom-empty-state-no-origins">
              <div className="dom-empty-icon-wrap">
                <ErrorOutlined sx={{ color: "#1976d2", fontSize: 24 }} />
              </div>
              <p className="dom-empty-title">
                {originFilterText.trim()
                  ? "No Matching Origins"
                  : `No Origins Added Yet for ${product}`}
              </p>
              <p className="dom-empty-body">
                {originFilterText.trim()
                  ? "Try a different filter value."
                  : "Use the + Add Origin button to add a Koch or competitor origin to the pricing table."}
              </p>
            </div>
          )}

          {filteredRows.map((origin, index) => (
            <OriginRow
              key={getRowKey(origin)}
              origin={origin}
              index={index}
              filteredRows={filteredRows}
              rows={rows}
              newlyAddedOrigins={newlyAddedOrigins}
              originTypeFilter={originTypeFilter}
              selectedMonths={selectedMonths}
              activeSplitMonths={activeSplitMonths}
              getSplitMonthsForRow={getSplitMonthsForRow}
              getModeRows={getModeRows}
              isEditingPrices={isEditingPrices}
              editingProductKey={editingProductKey}
              editingRowKey={editingRowKey}
              selectedProduct={product}
              dragOverRowKey={dragOverRowKey}
              dragState={dragState}
              monthGridTemplateColumns={monthGridTemplateColumns}
              registerMonthScroller={registerMonthScroller}
              onMonthScroll={syncMonthScroll}
              registerRowRef={registerRowRef}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleRowDragOver={handleRowDragOver}
              handleRowDrop={handleRowDrop}
              openInsightsPanel={openInsightsPanel}
              onCopyToClipboard={(clickedOrigin) => {
                setSendPanelOrigin(clickedOrigin);
                setShowSendPanel(true);
              }}
              onRequestDelete={handleRequestDeleteOrigin}
              activeCellKey={activeCellKey}
              recentlySavedKeys={recentlySavedKeys}
              dragHoverPriceKey={dragHoverPriceKey}
              draftPrices={draftPrices}
              draftPriceShowAs={draftPriceShowAs}
              isPriceDragCopying={isPriceDragCopying}
              priceInputRefs={priceInputRefs}
              getPriceInputKey={getPriceInputKey}
              getPriceInputValue={getPriceInputValue}
              getPriceShowAsValue={getPriceShowAsValue}
              getDisplayPriceValue={getDisplayPriceValue}
              handlePriceCellClick={handlePriceCellClick}
              handlePriceInputChange={handlePriceInputChange}
              handlePriceInputKeyDown={handlePriceInputKeyDown}
              handlePriceInputBlur={handlePriceInputBlur}
              handlePriceShowAsChange={handlePriceShowAsChange}
              handlePriceShowAsChangeAndSave={handlePriceShowAsChangeAndSave}
              handleToggleSplitMonth={handleToggleSplitMonth}
              handleRevertEditingPrices={handleRevertEditingPrices}
              applyDraggedPriceToInput={applyDraggedPriceToInput}
              startPriceDragCopy={startPriceDragCopy}
            />
          ))}

          <OriginManagerBottomScrollbar
            monthColumns={monthColumns}
            monthGridTemplateColumns={monthGridTemplateColumns}
            bottomMonthScrollbarRef={bottomMonthScrollbarRef}
            onScroll={(event) =>
              syncMonthScroll("__bottom__", event.currentTarget.scrollLeft)
            }
          />
        </div>
      </div>

      <SaveDiscardModal
        show={showSaveDiscard}
        onCancel={handleCancelSaveDiscard}
        onDiscard={handleDiscardEdits}
        onSave={handleSaveEdits}
      />

      <AddProductPromptModal
        pendingAddProductPrompt={pendingAddProductPrompt}
        isAddingMissingProduct={isAddingMissingProduct}
        selectedAddProductOccupantKeys={selectedAddProductOccupantKeys}
        hasAddProductOccupantSelectionChanged={
          hasAddProductOccupantSelectionChanged
        }
        closeAddProductPrompt={closeAddProductPrompt}
        toggleAddProductOccupantSelection={toggleAddProductOccupantSelection}
        handleConfirmAddProductForOrigin={handleConfirmAddProductForOrigin}
      />

      {showSendPanel &&
        sendPanelOrigin &&
        createPortal(
          <div
            className="dom-save-discard-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Send Price Guidance"
          >
            <div className="dom-send-panel-modal">
              <ProductGuidancePanel
                sendPanelOrigin={sendPanelOrigin}
                selectedProduct={product}
                selectedMonths={selectedMonths}
                activeSplitMonths={activeSplitMonths}
                getSplitMonthsForRow={getSplitMonthsForRow}
                getMonthLabel={getMonthLabel}
                getModeRows={getModeRows}
                formatPriceByShowAs={formatPriceByShowAs}
                getDisplayPriceValue={getDisplayPriceValue}
                getPriceShowAsValue={getPriceShowAsValue}
                onClose={() => {
                  setShowSendPanel(false);
                  setSendPanelOrigin(null);
                }}
                savedNotification={savedNotification}
                failedNotification={failedNotification}
                onSaveAdditionalTypes={handleSaveAdditionalTypes}
                onCopyComplete={() => {
                  setShowSendPanel(false);
                  setSendPanelOrigin(null);
                }}
              />
            </div>
          </div>,
          document.body,
        )}

      {pendingUndoDelete && (
        <DeleteUndoToast
          locationName={pendingUndoDelete.locationName}
          onUndo={handleUndoDelete}
        />
      )}

      {isAddOriginTourVisible && addOriginTourSpotlightRect && (
        <AddOriginTourOverlay
          spotlightRect={addOriginTourSpotlightRect}
          onDismiss={dismissAddOriginTour}
        />
      )}
    </div>
  );
};

export default DesktopOriginManagerPanel;
